import { getTestAttributes, noop, slug } from '@chillibean/component-library';
import { clsx } from 'clsx';
import { PropTypes } from 'prop-types';
import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Key } from 'ts-key-enum';

const StandardCard = forwardRef((props, ref) => {
  const {
    active = true,
    children = undefined,
    clickHandler = noop,
    customStyle = undefined,
    identifier = undefined,
    objectId = undefined,
    rejected = false,
    title,
    titleClass = undefined,
    titleEllipsis = false,
    titleIcon = undefined,
    titleLink = undefined,
  } = props;

  const displayIconLink = () => {
    if (titleLink) {
      return <Link to={titleLink}>{titleIcon}</Link>;
    }
    return titleIcon;
  };

  const displayTitleLink = () => {
    if (titleLink) {
      return (
        <Link to={titleLink} className={titleClass || ''} {...getTestAttributes('standard-card-title')}>
          {title}
        </Link>
      );
    }
    return (
      <span className={titleClass || ''} {...getTestAttributes('standard-card-title')}>
        {title}
      </span>
    );
  };

  const displayCardContent = () => {
    if (React.Children.count(children) === 1) {
      return <div className="standard-card__info-item">{children}</div>;
    }

    return children.map((child, i) => {
      return (
        // https://github.com/jsx-eslint/eslint-plugin-react/issues/1123
        // eslint-disable-next-line react/no-array-index-key
        <div className="standard-card__info-item" key={i} {...getTestAttributes(`standard-card__info-item-${i}`)}>
          {child}
        </div>
      );
    });
  };

  const titleEllipsisClass = () => {
    return titleEllipsis ? 'standard-card__title-ellipsis-width' : '';
  };

  const onClick = (event) => {
    if (active) {
      clickHandler(event);
    }
  };

  const onKeyUp = (event) => {
    if (Key.Enter === event.key) {
      onClick(event);
    }
  };

  return (
    <div
      style={customStyle}
      ref={ref}
      className={`standard-card ${active ? '' : 'standard-card--inactive'}`}
      onClick={onClick}
      onKeyUp={onKeyUp}
      role="button"
      tabIndex={0}
      {...getTestAttributes('standard-card')}
    >
      <div
        className="standard-card__wrapper-link"
        {...getTestAttributes(`${slug(identifier || title)}-standard-card`)}
      />
      <div
        className={`${titleEllipsisClass()} standard-card__info`}
        {...getTestAttributes(`${slug(identifier || title)}-standard-card-text`)}
      >
        {titleIcon && <div className="standard-card__info-item">{displayIconLink()}</div>}
        <div
          className={`${titleEllipsisClass()} standard-card__info-item standard-card__info-item--title`}
          title={title}
        >
          {objectId && (
            <div
              className={clsx({
                'standard-card__info-item--id': true,
                'standard-card__info-item--rejected': rejected,
              })}
              {...getTestAttributes('standard-card-id')}
            >{`${rejected ? 'Rejected ' : ''}${objectId}`}</div>
          )}
          <div className={titleEllipsis ? 'standard-card__title-ellipsis' : ''}>{displayTitleLink()}</div>
        </div>
      </div>
      {React.Children.count(children) > 0 ? <div className="standard-card__info">{displayCardContent()}</div> : null}
    </div>
  );
});

StandardCard.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  clickHandler: PropTypes.func,
  customStyle: PropTypes.objectOf(PropTypes.string),
  identifier: PropTypes.string,
  objectId: PropTypes.number,
  rejected: PropTypes.bool,
  title: PropTypes.string.isRequired,
  titleClass: PropTypes.string,
  titleEllipsis: PropTypes.bool,
  titleIcon: PropTypes.element,
  titleLink: PropTypes.string,
};

export default StandardCard;
